import React, { createContext, useState, useContext, useEffect } from "react";
import { createEntryComment, updateEntryComment, createEntryState, updateEntryState, getEntryStates, getWindData, getWindEdgeRiskData, fetchWeatherData } from "../../../api/services/dfr";
const DFRContext = createContext();

const windSpeeds = [
      { name: '0.5 - 2.5', color: 'rgb(85, 222, 246)', min: 0.0, max: 2.5, category: 'Svag vind', description: 'Vindstilla' },
      { name: '2.5 - 4.5', color: 'rgb(126, 246, 85)', min: 2.5, max: 4.5, category: 'Lätt bris', description: 'Rök stiger rakt upp' },
      { name: '4.5 - 6.5', color: 'rgb(246, 225, 85)', min: 4.5, max: 6.5, category: 'Måttlig bris', description: 'Löv börjar röra sig' },
      { name: '6.5 - 8.5', color: 'rgb(246, 182, 85)', min: 6.5, max: 8.5, category: 'Frisk bris', description: 'Tunna grena rör sig' },
      { name: '8.5 - 10.5', color: 'rgb(246, 126, 85)', min: 8.5, max: 10.5, category: 'Hård bris', description: 'Stora grena rör sig' },
      { name: '10.5 - 12.5', color: 'rgb(246, 91, 85)', min: 10.5, max: 12.5, category: 'Hård vind', description: 'Trädtoppar svajar' },
      { name: '12.5 - 14.5', color: 'rgb(246, 88, 222)', min: 12.5, max: 14.5, category: 'Risk till storm', description: 'Svårt att gå mot vinden' },
      { name: '14.5 - 20', color: 'rgb(196, 86, 246)', min: 14.5, max: 20, category: 'Storm', description: 'Risk för skador' },
      { name: '> 20', color: 'rgb(125, 85, 246)', min: 20, max: 50, category: 'Extrem storm', description: 'Extrem risk för skador' }
    ]

export const DFRProvider = ({ children }) => {
  const [wind, setWind] = useState({});
  const [windEdgeRisks, setWindEdgeRisks] = useState({});
  const [states, setStates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [weatherData, setWeatherData] = useState({});


  const getWeatherData = async (lmID, designation) => {
    const date = new Date();
    const today = date.toISOString().split('T')[0];
    const yesterday = new Date(date.setDate(date.getDate() - 1)).toISOString().split('T')[0];
    const thirtyDaysAgo = new Date(date.setDate(date.getDate() - 30)).toISOString().split('T')[0];

    const weatherPeriods = [
      {
        startDate: yesterday,
        // startDate: "2025-06-12", 
        endDate: today
      },
      {
        startDate: thirtyDaysAgo,
        endDate: today
      },
    ]
    

    const [weatherToday, weatherHistoric] = await Promise.all(weatherPeriods.map(async (period) => {
      const response = await fetchWeatherData({ lmID, startDate: period.startDate, endDate: period.endDate });
      return response.data;
    }));

    
    setWeatherData(prevWeatherData => ({
      ...prevWeatherData,
      [lmID]: {
        designation,
        today: weatherToday,
        historic: weatherHistoric
      }
    }));
  };

  const getStates = async () => {
    const resp = await getEntryStates()
    const respData = resp.data

    if(resp.ok) {
      setStates(respData)
    } 
  }

  useEffect(() => {
    getStates()
  }, [])

  const addComment = async (entryID, comment) => {
    const resp = await createEntryComment(entryID, comment)
    const respData = resp.data

    if(resp.ok) {
      setEntries(entries.map(entry => entry.id === entryID ? {...entry, comments: [respData, ...entry.comments]} : entry))
    }
    return resp.ok
  };

  const updateComment = async (entryID, comment) => {
    const resp = await updateEntryComment(comment)
    const respData = resp.data

    if(resp.ok) {
      setEntries(prevEntries => prevEntries.map((entry) => entry.id === entryID ? {...entry, comments: entry.comments.map(c => c.id === comment.id ? respData : c)} : entry));
    }
    return resp.ok
  };

  const addState = async (entryID, state) => {
    const resp = await createEntryState(entryID, state)
    const respData = resp.data
    if(resp.ok) {
      setEntries(prevEntries => prevEntries.map(entry => entry.id === entryID ? {...entry, states: [respData, ...entry.states]} : entry))
    }
    return resp.ok
  };

  const updateState = async (entryID, state) => {
    const resp = await updateEntryState(state)
    const respData = resp.data

    if(resp.ok) {
      setEntries(prevEntries => prevEntries.map((entry) => entry.id === entryID ? {...entry, states: entry.states.map(c => c.id === state.id ? respData : c)} : entry));
    }
    return resp.ok
  };

  const getWind = async (ref, geom, type) => {
    setWind(prevWind => ({...prevWind,  [ref]: {...prevWind[ref], [type]: undefined }}))

    const now = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(now.getDate() - 30) // 30 days
    const manyYearsAgo = new Date()
    manyYearsAgo.setDate(now.getDate() - 3650) // 10 years

    const dataRequests = {
      month: {
        ref: ref,
        geom: geom,
        start_date: thirtyDaysAgo.toISOString(),
        end_date: now.toISOString(),
      },
      years: {
        ref: ref,
        geom: geom,
        start_date: manyYearsAgo.toISOString(),
        end_date: (new Date('2024-12-31')).toISOString(),
      }
    }

    if(!dataRequests[type]) {
      setWind(prevWind => ({...prevWind, [ref]: {...prevWind[ref], [type]: {error: 'Något gick fel.'} }}))
      return false
    }

    const resp = await getWindData({data: dataRequests[type]})

    const directions = [
      { name: 'N', min: 348.75, max: 11.25 }, 
      { name: 'NNE', min: 11.25, max: 33.75 },
      { name: 'NE', min: 33.75, max: 56.25 },
      { name: 'ENE', min: 56.25, max: 78.75 },
      { name: 'E', min: 78.75, max: 101.25 },
      { name: 'ESE', min: 101.25, max: 123.75 },
      { name: 'SE', min: 123.75, max: 146.25 },
      { name: 'SSE', min: 146.25, max: 168.75 },
      { name: 'S', min: 168.75, max: 191.25 },
      { name: 'SSW', min: 191.25, max: 213.75 },
      { name: 'SW', min: 213.75, max: 236.25 },
      { name: 'WSW', min: 236.25, max: 258.75 },
      { name: 'W', min: 258.75, max: 281.25 },
      { name: 'WNW', min: 281.25, max: 303.75 },
      { name: 'NW', min: 303.75, max: 326.25 },
      { name: 'NNW', min: 326.25, max: 348.75 }
    ];

    let sortedData = windSpeeds.reduce((accumulator, currentSpeed) => {
      const newDirectionsObject = {};
      for (const dir of directions) {
        newDirectionsObject[dir.name] = 0;
      }
      
      accumulator[currentSpeed.name] = newDirectionsObject;
      return accumulator;
    }, {});

    const getSpeedName = speed => {
      for (const s of windSpeeds) {
        if (speed >= s.min && speed < s.max) {
          return s.name;
        }
      }
      return null;
    }


    if(resp.ok) {
      const respData = resp.data
      const filteredData = respData.filter(i => i.speed >= 0.5)

      filteredData.filter(i => i.speed != 0.0).map(i => {
        const speedName = getSpeedName(i.speed)
        if(i.direction >= 348.75 || i.direction <= 11.25) {
          sortedData[speedName][directions[0].name]++
        } else {
          for (const direction of directions) {
            if (i.direction >= direction.min && i.direction < direction.max) {
              sortedData[speedName][direction.name]++
            }
          }
        }
      })

      setWind(prevWind => ({...prevWind, [ref]: {...prevWind[ref], [type]: {total: filteredData.length, data: sortedData}}}))
    } else {
      setWind(prevWind => ({...prevWind,  [ref]: {...prevWind[ref], [type]: resp.error ? {error: resp.error} : {error: 'Något gick fel.'} }}))
    }

    return resp.ok
  }

  const getWindEdgeRisk = async(ref) => {
    setWindEdgeRisks(prevWindEdgeRisk => ({...prevWindEdgeRisk, [ref]: undefined}))

    const resp = await getWindEdgeRiskData({ref:ref})

    if(resp.ok) {
      const data = resp.data
      setWindEdgeRisks(prevWindEdgeRisk => ({...prevWindEdgeRisk, [ref]: data}))
    } else {
      setWindEdgeRisks(prevWindEdgeRisk => ({...prevWindEdgeRisk, [ref]: resp.error ? {error: resp.error} : {error: 'Något gick fel.'} }))
    }
  }


  return (
    <DFRContext.Provider value={{wind, windSpeeds, getWind, windEdgeRisks, getWindEdgeRisk, states, entries, setEntries, updateComment, addComment, updateState, addState, weatherData, getWeatherData }}>
      {children}
    </DFRContext.Provider>
  );
};

export const useDFR = () => useContext(DFRContext);

export default DFRContext;
